/**
 * useNudge Composable
 *
 * Manages nudge functionality for reminding inactive players during discard phase.
 *
 * Features:
 * - Auto-nudge timer (30s inactivity during discard phase)
 * - Manual nudge with cooldown (prevent spam)
 * - Nudge received handler with sound/animation trigger
 *
 * @example
 * const nudge = useNudge(isMyTurn, turnPhase, currentPlayerId, socket)
 * // Check if can nudge another player
 * if (nudge.canNudge.value) nudge.sendNudge()
 * // React to nudge animation state
 * :class="{ 'nudged': nudge.isNudged.value }"
 */

import { ref, watch, onUnmounted, computed, type Ref, type ComputedRef } from 'vue'
import { useSounds } from './useSounds'

// Constants
const AUTO_NUDGE_DELAY = 30000 // 30 seconds before auto-nudge
const NUDGE_COOLDOWN = 10000 // 10 seconds between manual nudges
const NUDGE_VISIBLE_DELAY = 15000 // 15 seconds before nudge button appears for other players
const ANIMATION_DURATION = 800 // 800ms for sonic boom pulse
const DEBOUNCE_RECEIVED = 2000 // 2 seconds debounce for received nudges

export interface NudgeData {
  target_id: string
  nudger_id?: string
  is_auto?: boolean
}

export interface UseNudgeReturn {
  /** Whether the nudge animation is currently playing */
  isNudged: Ref<boolean>
  /** Whether this player can send a nudge (not their turn, in discard phase, cooldown passed) */
  canNudge: ComputedRef<boolean>
  /** Whether the nudge button should be visible (60s timer elapsed) */
  canShowNudgeButton: Ref<boolean>
  /** Seconds remaining on nudge cooldown */
  nudgeCooldownRemaining: ComputedRef<number>
  /** Send a nudge to the current player */
  sendNudge: () => void
  /** Reset activity timer (call when player takes action) */
  resetActivity: () => void
  /** Handle received nudge event from WebSocket */
  handleNudgeReceived: (data: NudgeData) => void
  /** Manually trigger nudge animation (for testing) */
  triggerNudgeAnimation: () => void
}

/**
 * Create nudge composable for managing player reminders
 *
 * @param isMyTurn - Computed ref indicating if it's the current player's turn
 * @param turnPhase - Computed ref with current turn phase ('draw' | 'meld' | 'discard')
 * @param currentPlayerId - Computed ref with current player's ID
 * @param emitNudge - Function to emit nudge event via socket
 */
export function useNudge(
  isMyTurn: ComputedRef<boolean> | Ref<boolean>,
  turnPhase: ComputedRef<string> | Ref<string>,
  currentPlayerId: ComputedRef<string | undefined> | Ref<string | undefined>,
  emitNudge: (() => void) | null
): UseNudgeReturn {
  const sounds = useSounds()

  // State
  const isNudged = ref(false)
  const lastNudgeTime = ref(0)
  const lastReceivedNudgeTime = ref(0)
  const canShowNudgeButton = ref(false)

  // Timers (use ReturnType for browser compatibility - no NodeJS types)
  let autoNudgeTimer: ReturnType<typeof setTimeout> | null = null
  let animationTimer: ReturnType<typeof setTimeout> | null = null
  let visibilityTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Whether this player can send a nudge
   * - Not their turn
   * - Game is in discard phase
   * - Cooldown has passed
   */
  const canNudge = computed(() => {
    const now = Date.now()
    const cooldownPassed = now - lastNudgeTime.value > NUDGE_COOLDOWN
    return !isMyTurn.value && turnPhase.value === 'discard' && cooldownPassed
  })

  /**
   * Seconds remaining on nudge cooldown (for UI display)
   */
  const nudgeCooldownRemaining = computed(() => {
    const elapsed = Date.now() - lastNudgeTime.value
    return Math.max(0, Math.ceil((NUDGE_COOLDOWN - elapsed) / 1000))
  })

  /**
   * Start auto-nudge timer (only for current player during discard phase)
   */
  function startAutoNudgeTimer(): void {
    clearAutoNudgeTimer()

    // Only start timer if it's my turn and I'm in discard phase
    if (!isMyTurn.value || turnPhase.value !== 'discard') return

    autoNudgeTimer = setTimeout(() => {
      // Trigger self-nudge (visual reminder to self)
      triggerNudgeAnimation()
    }, AUTO_NUDGE_DELAY)
  }

  /**
   * Clear auto-nudge timer
   */
  function clearAutoNudgeTimer(): void {
    if (autoNudgeTimer) {
      clearTimeout(autoNudgeTimer)
      autoNudgeTimer = null
    }
  }

  /**
   * Start visibility timer (60s before nudge button appears for other players)
   */
  function startVisibilityTimer(): void {
    clearVisibilityTimer()

    // Only start if not my turn and in discard phase
    if (isMyTurn.value || turnPhase.value !== 'discard') {
      console.log('[useNudge] startVisibilityTimer: skipped (isMyTurn:', isMyTurn.value, 'turnPhase:', turnPhase.value, ')')
      return
    }

    console.log('[useNudge] startVisibilityTimer: starting', NUDGE_VISIBLE_DELAY, 'ms timer')
    visibilityTimer = setTimeout(() => {
      console.log('[useNudge] Visibility timer fired - showing nudge button')
      canShowNudgeButton.value = true
    }, NUDGE_VISIBLE_DELAY)
  }

  /**
   * Clear visibility timer and hide nudge button
   */
  function clearVisibilityTimer(): void {
    if (visibilityTimer) {
      clearTimeout(visibilityTimer)
      visibilityTimer = null
    }
    canShowNudgeButton.value = false
  }

  /**
   * Send a nudge to the current player via WebSocket
   */
  function sendNudge(): void {
    console.log('[useNudge] sendNudge called (canNudge:', canNudge.value, 'hasEmitFn:', !!emitNudge, ')')
    if (!canNudge.value || !emitNudge) {
      console.log('[useNudge] sendNudge: blocked - canNudge:', canNudge.value)
      return
    }

    console.log('[useNudge] Emitting nudge_player event')
    emitNudge()
    lastNudgeTime.value = Date.now()

    // Hide button and restart 60s timer
    startVisibilityTimer()
  }

  /**
   * Trigger the nudge animation (shake + sound)
   */
  function triggerNudgeAnimation(): void {
    isNudged.value = true
    sounds.playTurnNotification()

    // Clear animation after duration
    if (animationTimer) {
      clearTimeout(animationTimer)
    }
    animationTimer = setTimeout(() => {
      isNudged.value = false
    }, ANIMATION_DURATION)
  }

  /**
   * Handle receiving a nudge from WebSocket
   */
  function handleNudgeReceived(data: NudgeData): void {
    console.log('[useNudge] handleNudgeReceived:', data, 'myId:', currentPlayerId.value)

    // Only react if I'm the target
    if (data.target_id !== currentPlayerId.value) {
      console.log('[useNudge] handleNudgeReceived: not my nudge (target:', data.target_id, '!= me:', currentPlayerId.value, ')')
      return
    }

    // Debounce: ignore if received another nudge within 2 seconds
    const now = Date.now()
    if (now - lastReceivedNudgeTime.value < DEBOUNCE_RECEIVED) {
      console.log('[useNudge] handleNudgeReceived: debounced')
      return
    }
    lastReceivedNudgeTime.value = now

    console.log('[useNudge] Triggering nudge animation!')
    triggerNudgeAnimation()
  }

  /**
   * Reset activity timer (call when player takes any action)
   * Restarts the auto-nudge countdown
   */
  function resetActivity(): void {
    if (isMyTurn.value && turnPhase.value === 'discard') {
      startAutoNudgeTimer()
    }
  }

  // Watch for turn/phase changes to manage auto-nudge timer and visibility timer
  watch(
    [() => isMyTurn.value, () => turnPhase.value],
    ([myTurn, phase]) => {
      console.log('[useNudge] Watcher triggered - myTurn:', myTurn, 'phase:', phase)

      if (myTurn && phase === 'discard') {
        // My turn in discard phase: start auto-nudge, clear visibility
        console.log('[useNudge] My turn in discard phase - starting auto-nudge timer')
        startAutoNudgeTimer()
        clearVisibilityTimer()
      } else if (!myTurn && phase === 'discard') {
        // Not my turn but in discard phase: start visibility timer
        console.log('[useNudge] Not my turn but in discard phase - starting visibility timer')
        clearAutoNudgeTimer()
        startVisibilityTimer()
      } else {
        // Any other state: clear both timers
        console.log('[useNudge] Other state - clearing both timers')
        clearAutoNudgeTimer()
        clearVisibilityTimer()
      }
    },
    { immediate: true }
  )

  // Cleanup on unmount
  onUnmounted(() => {
    clearAutoNudgeTimer()
    clearVisibilityTimer()
    if (animationTimer) {
      clearTimeout(animationTimer)
    }
  })

  return {
    isNudged,
    canNudge,
    canShowNudgeButton,
    nudgeCooldownRemaining,
    sendNudge,
    resetActivity,
    handleNudgeReceived,
    triggerNudgeAnimation,
  }
}
